import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortDown,
  faSortUp,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { logError, LogCategory } from "utils/sentryLogger";
import { triggerCsvDownload } from "../../Admin/CSReports/csReportsApi";
import { trackClickedButton } from "utils/analytics";
import { Tooltip } from "components/shared/Tooltip/Tooltip";

const SKELETON_ROWS = 8;
const INITIAL_VISIBLE = 50;
const VISIBLE_CHUNK = 50;

const renderCell = (value, meta) => {
  if (meta?.formatter) return meta.formatter(value);
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  }
  return String(value);
};

// Contiguous columns sharing `meta.band` merge into one group header cell;
// columns without a band get a blank one. Null when no column has a band, so
// tables without bands keep a single header row.
export const bandGroups = (columns) => {
  if (!columns.some(([, , meta]) => meta?.band)) return null;
  return columns.reduce((groups, [key, , meta]) => {
    const band = meta?.band || null;
    const last = groups[groups.length - 1];
    if (last && last.band === band) last.span += 1;
    else groups.push({ key, band, span: 1 });
    return groups;
  }, []);
};

const compareValues = (a, b) => {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

/**
 * One report tab: fetches through react-query whenever the shared filters
 * change (a revisited filter combination paints instantly from cache while
 * revalidating; the skeleton only covers an uncached one), renders a sortable
 * Student Activity-style table and a CSV export. The server's row order is
 * the default until a header is clicked. `chart(rows)` renders above the
 * table once rows are in; `toolbar(data)` renders in the top row and sees the
 * whole response, for controls whose options come from it.
 */
export const ReportTab = ({
  params,
  fetchJson,
  fetchCsv,
  errorTag,
  columns,
  chart,
  toolbar,
  tabName,
  reportScope,
}) => {
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const {
    data,
    error,
    isInitialLoading: loading,
  } = useQuery({
    queryKey: ["school-report", errorTag, params],
    queryFn: () => fetchJson(params),
    enabled: Boolean(params),
    staleTime: 0,
    onError: (e) =>
      logError(`School Reports: ${errorTag} fetch failed`, e, {
        category: LogCategory.ERROR,
      }),
  });

  const rows = useMemo(() => {
    const raw = data?.rows || [];
    if (!sortBy) return raw;
    // A descending comparator (not reverse()) keeps the sort stable: rows with
    // equal values hold their relative order across toggles.
    return [...raw].sort((a, b) =>
      sortOrder === "asc"
        ? compareValues(a[sortBy], b[sortBy])
        : compareValues(b[sortBy], a[sortBy])
    );
  }, [data, sortBy, sortOrder]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((order) => (order === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortOrder("asc");
  };

  // The full dataset stays in memory (sorting and CSV work over all of it);
  // only a growing window of rows reaches the DOM, so a district-sized
  // report can't freeze the tab.
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const sentinelRef = useRef(null);
  const visibleRows = rows.slice(0, visibleCount);
  const hasMore = visibleCount < rows.length;

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [data]);

  useEffect(() => {
    if (!hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) => count + VISIBLE_CHUNK);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, visibleCount]);

  const handleDownload = async () => {
    // Tracked on click, not on success: this measures intent to export.
    trackClickedButton("Export CSV", {
      page_name: "School Reports",
      tab: tabName,
      report_scope: reportScope,
    });
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetchCsv(params);
      await triggerCsvDownload(response);
    } catch (e) {
      setDownloadError(e?.detail || e?.message || "Failed to download CSV.");
      logError(`School Reports: ${errorTag} CSV failed`, e, {
        category: LogCategory.ERROR,
      });
    } finally {
      setDownloading(false);
    }
  };

  const groups = bandGroups(columns);

  const errorMessage =
    downloadError || (error && (error.detail || error.message || "Failed to load report."));

  return (
    <div>
      {errorMessage && <Alert variant="danger">{String(errorMessage)}</Alert>}

      <TopRow>
        {toolbar && <Toolbar>{toolbar(data)}</Toolbar>}
        <TopRowRight>
        {!loading && (
          <RowCount>
            {hasMore
              ? `Showing ${visibleRows.length.toLocaleString()} of ${rows.length.toLocaleString()} rows`
              : `${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}`}
          </RowCount>
        )}
        <ExportButton onClick={handleDownload} disabled={!params || downloading}>
          {downloading && <FontAwesomeIcon icon={faSpinner} spin />}
          Export CSV
        </ExportButton>
        </TopRowRight>
      </TopRow>

      {chart && !loading && (data?.rows || []).length > 0 && chart(data.rows)}

      <TableWrapper>
        <ReportTable>
          <thead>
            {groups && (
              <tr>
                {groups.map(({ key, band, span }) =>
                  band ? (
                    <BandHeader scope="colgroup" colSpan={span} key={key}>
                      {band}
                    </BandHeader>
                  ) : (
                    <BlankBandHeader colSpan={span} key={key} />
                  )
                )}
              </tr>
            )}
            <tr>
              {columns.map(([key, header, meta]) =>
                loading ? (
                  <SkeletonHeaderCell scope="col" key={key}>
                    <SkeletonBar $w="90px" $light />
                  </SkeletonHeaderCell>
                ) : (
                  <SortableHeader scope="col" key={key} onClick={() => handleSort(key)}>
                    {meta?.tip ? (
                      <Tooltip id={`report-tip-${key}`} tip={meta.tip} placement="bottom">
                        <span tabIndex={0} aria-describedby={`report-tip-${key}`}>
                          {header}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>{header}</span>
                    )}
                    <SortIcon
                      icon={
                        sortBy === key
                          ? sortOrder === "asc"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      $isActive={sortBy === key}
                    />
                  </SortableHeader>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: SKELETON_ROWS }, (_, row) => (
                <TableRow key={row}>
                  {columns.map(([key]) => (
                    <SkeletonBodyCell key={key}>
                      <SkeletonBar $w="70px" />
                    </SkeletonBodyCell>
                  ))}
                </TableRow>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <EmptyCell colSpan={columns.length}>
                  No sessions in this date range for the selected filters.
                </EmptyCell>
              </tr>
            )}
            {!loading &&
              visibleRows.map((row, index) => (
                <TableRow key={index}>
                  {columns.map(([key, , meta]) => (
                    <BodyCell key={key}>{renderCell(row[key], meta)}</BodyCell>
                  ))}
                </TableRow>
              ))}
          </tbody>
        </ReportTable>
        {hasMore && <Sentinel ref={sentinelRef}>Loading more rows...</Sentinel>}
      </TableWrapper>
    </div>
  );
};

const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 12px;
`;

// Up to two filter-bar dropdowns side by side, each sized like one in the
// filter bar: domain labels carry a code and a full name ("5.NF Number and
// Operations—Fractions"), which needs the room. The dropdowns split the width
// themselves (their container is flex: 1).
const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  flex: 1;
  max-width: 772px;
`;

const TopRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
`;

const RowCount = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: #4F4F4F;
`;

const Sentinel = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #4F4F4F;
  text-align: center;
  padding: 12px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  height: 40px;
  border-radius: 6px;
  border: none;
  background-color: ${colors.blue500};
  color: ${colors.gray50};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const ReportTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const headerCellStyles = `
  background-color: #002E19;
  color: white;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
`;

const SortableHeader = styled.th`
  ${headerCellStyles}
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #003D20;
  }
`;

const BandHeader = styled.th`
  ${headerCellStyles}
  border-left: 2px solid white;
  border-right: 2px solid white;
  border-bottom: 1px solid #858B87;
`;

const BlankBandHeader = styled.th`
  ${headerCellStyles}
`;

const SortIcon = styled(FontAwesomeIcon)`
  margin-left: 6px;
  font-size: 12px;
  opacity: ${(props) => (props.$isActive ? 1 : 0.5)};
`;

const TableRow = styled.tr`
  background-color: #FFFEFE;
  border-bottom: 1px solid #858B87;
`;

const bodyCellStyles = `
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #4F4F4F;
  padding: 10px 12px;
  white-space: nowrap;
  border-bottom: 1px solid #858B87;
`;

const BodyCell = styled.td`
  ${bodyCellStyles}
`;

const SkeletonHeaderCell = styled.th`
  ${headerCellStyles}
`;

const SkeletonBodyCell = styled.td`
  ${bodyCellStyles}
  background-color: ${colors.gray50};
`;

const SkeletonBar = styled.div`
  @keyframes skeleton-pulse {
    0% { opacity: 0.6; }
    50% { opacity: 0.3; }
    100% { opacity: 0.6; }
  }
  width: ${(p) => p.$w || "60px"};
  height: 14px;
  border-radius: 4px;
  background: ${(p) => (p.$light ? "rgba(255, 255, 255, 0.5)" : colors.milkGlass600)};
  animation: skeleton-pulse 1.5s ease-in-out infinite;
  margin: 0 auto;
`;

const EmptyCell = styled.td`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  color: ${colors.milkGlass950};
  text-align: center;
  padding: 32px 16px;
  background-color: #FFFEFE;
`;
